import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ImageModal from './components/ImageModal';
import DatasetSelector from './components/annotation/DatasetSelector';
import AnnotationLotsTable from './components/annotation/AnnotationLotsTable';
import AnnotationHistoryModal from './components/annotation/AnnotationHistoryModal';
import { useAuth } from './context/AuthContext';

/**
 * AnnotationPage — "Annotation tool"
 *
 * Per-auction-dataset annotation screen. Lots are loaded from the database
 * (via `/annotation_dataset_lots`). The DB is populated on demand via
 * `/annotation_sync_dataset`, which snapshots the dataset from blob storage
 * into the `DatasetLots` table.
 *
 * Styling mirrors the Test Rig page (same outer container, title, and
 * dataset badge) for a consistent look.
 */
const AnnotationPage = () => {
  const { user: currentUser } = useAuth();
  const [datasets, setDatasets] = useState([]);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(true);
  const [isRefreshingDatasets, setIsRefreshingDatasets] = useState(false);
  const [datasetLoadError, setDatasetLoadError] = useState('');
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [datasetSearch, setDatasetSearch] = useState('');
  const [isDatasetDropdownOpen, setIsDatasetDropdownOpen] = useState(false);
  const datasetDropdownRef = useRef(null);
  const lotsFetchSequenceRef = useRef(0);

  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [syncing, setSyncing] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedLotNum, setSelectedLotNum] = useState(null);

  const [historyLot, setHistoryLot] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const refreshDatasets = async () => {
    if (isRefreshingDatasets) return;
    setIsRefreshingDatasets(true);
    setDatasetLoadError('');
    try {
      const response = await axios.post('/refresh_test_datasets');
      const nextDatasets = Array.isArray(response?.data?.datasets) ? response.data.datasets : [];
      setDatasets(nextDatasets);
      if (nextDatasets.length === 0) {
        setSelectedDataset(null);
        setDatasetSearch('');
        return;
      }
      const currentPrefix = selectedDataset?.prefix || '';
      const matchingSelection = nextDatasets.find((entry) => (entry.prefix || '') === currentPrefix) || null;
      if (matchingSelection) {
        setSelectedDataset(matchingSelection);
        setDatasetSearch(matchingSelection.name || matchingSelection.prefix || '');
      } else {
        setSelectedDataset(null);
        setDatasetSearch('');
      }
    } catch (error) {
      console.error('Error refreshing annotation datasets:', error);
      setDatasetLoadError(error?.response?.data?.message || error?.message || 'Failed to refresh datasets.');
    } finally {
      setIsRefreshingDatasets(false);
    }
  };

  const fetchDatasets = async () => {
    setIsLoadingDatasets(true);
    setDatasetLoadError('');
    try {
      const response = await axios.get('/test_datasets');
      const nextDatasets = Array.isArray(response?.data?.datasets) ? response.data.datasets : [];
      setDatasets(nextDatasets);

      if (nextDatasets.length === 0) {
        setSelectedDataset(null);
        setDatasetSearch('');
        return;
      }

      const currentPrefix = selectedDataset?.prefix || '';
      const matchingSelection = nextDatasets.find((entry) => (entry.prefix || '') === currentPrefix) || null;

      if (matchingSelection) {
        setSelectedDataset(matchingSelection);
        setDatasetSearch(matchingSelection.name || matchingSelection.prefix || '');
        return;
      }

      setSelectedDataset(null);
      setDatasetSearch('');
    } catch (error) {
      console.error('Error loading annotation datasets:', error);
      setDatasets([]);
      setSelectedDataset(null);
      setDatasetSearch('');
      setDatasetLoadError(error?.response?.data?.message || error?.message || 'Failed to load datasets.');
    } finally {
      setIsLoadingDatasets(false);
    }
  };

  const fetchLots = async (prefix) => {
    const requestSequence = ++lotsFetchSequenceRef.current;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/annotation_dataset_lots', {
        params: { dataset_prefix: prefix || '' },
      });
      if (requestSequence !== lotsFetchSequenceRef.current) return;
      const next = Array.isArray(response?.data?.lots) ? response.data.lots : [];
      setLots(next);
      const nextDrafts = {};
      next.forEach((lot) => {
        const key = String(lot.lot_number || '');
        if (!key) return;
        const ref = lot.refactored_actual;
        nextDrafts[key] = ref === null || ref === undefined ? '' : String(ref);
      });
      setDrafts(nextDrafts);
    } catch (e) {
      if (requestSequence !== lotsFetchSequenceRef.current) return;
      setError(e?.response?.data?.message || e.message || 'Failed to load lots for this auction.');
      setLots([]);
    } finally {
      if (requestSequence !== lotsFetchSequenceRef.current) return;
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatasets();
  }, []);

  useEffect(() => {
    if (!selectedDataset) {
      lotsFetchSequenceRef.current += 1;
      setLots([]);
      setDrafts({});
      setLoading(false);
      return;
    }
    fetchLots(selectedDataset?.prefix || '');
  }, [selectedDataset]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datasetDropdownRef.current && !datasetDropdownRef.current.contains(event.target)) {
        setIsDatasetDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const normalizedDatasetSearch = datasetSearch.trim().toLowerCase();
  const filteredDatasets = datasets.filter((dataset) => {
    if (!normalizedDatasetSearch) return true;
    const name = (dataset?.name || '').toLowerCase();
    const prefix = (dataset?.prefix || '').toLowerCase();
    return name.includes(normalizedDatasetSearch) || prefix.includes(normalizedDatasetSearch);
  });

  const selectDataset = (dataset) => {
    if (!dataset) return;
    setSelectedDataset(dataset);
    setDatasetSearch(dataset.name || dataset.prefix || '');
    setIsDatasetDropdownOpen(false);
  };

  const datasetBadgeText = selectedDataset
    ? `${selectedDataset.name || selectedDataset.prefix || 'Container root'}${
        selectedDataset.data_points !== undefined ? ` (${selectedDataset.data_points} data points)` : ''
      }`
    : (isLoadingDatasets
      ? 'Loading datasets...'
      : (datasetLoadError
        ? 'Dataset load failed'
        : (datasets.length > 0 ? 'Select a dataset' : 'No datasets available')));

  const onDraftChange = (lotNumber, value) => {
    setDrafts((prev) => ({ ...prev, [lotNumber]: value }));
  };

  const saveLot = async (lot) => {
    const lotNumber = String(lot.lot_number || '');
    if (!lotNumber) return;
    const raw = drafts[lotNumber];
    const trimmed = typeof raw === 'string' ? raw.trim() : raw;
    let payloadValue;
    if (trimmed === '' || trimmed === null || trimmed === undefined) {
      payloadValue = null;
    } else {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setError(`Lot ${lotNumber}: actual override must be a non-negative number.`);
        return;
      }
      payloadValue = parsed;
    }

    setSaving((prev) => ({ ...prev, [lotNumber]: true }));
    setError('');
    try {
      const response = await axios.post('/annotation_update_actual', {
        dataset_prefix: selectedDataset?.prefix || '',
        lot_number: lotNumber,
        refactored_actual: payloadValue,
      });
      const saved = response?.data?.refactored_actual;
      const savedByUserId = response?.data?.annotated_by_user_id;
      const savedByUsername = response?.data?.annotated_by_username;
      setLots((prev) => prev.map((item) =>
        String(item.lot_number || '') === lotNumber
          ? {
              ...item,
              refactored_actual: saved === undefined ? payloadValue : saved,
              annotated_by_user_id: savedByUserId !== undefined ? savedByUserId : (currentUser?.id ?? null),
              annotated_by_username: savedByUsername !== undefined ? savedByUsername : (currentUser?.username ?? null),
            }
          : item
      ));
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to save actual override.');
    } finally {
      setSaving((prev) => ({ ...prev, [lotNumber]: false }));
    }
  };

  const syncDataset = async () => {
    if (syncing) return;
    setSyncing(true);
    setError('');
    try {
      await axios.post('/annotation_sync_dataset', { dataset_prefix: selectedDataset?.prefix || '' });
      await fetchLots(selectedDataset?.prefix || '');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to sync dataset from storage.');
    } finally {
      setSyncing(false);
    }
  };

  const openHistory = async (lotNumber) => {
    const prefix = selectedDataset?.prefix || '';
    if (!prefix || !lotNumber) return;
    setHistoryLot(lotNumber);
    setHistoryEntries([]);
    setHistoryError('');
    setHistoryLoading(true);
    try {
      const response = await axios.get('/annotation_history', {
        params: { dataset_prefix: prefix, lot_number: lotNumber, limit: 500 },
      });
      const entries = Array.isArray(response?.data?.history) ? response.data.history : [];
      // History tracking was added after the annotation tool shipped, so the
      // earliest recorded entry may be an `update` whose `previous` value was
      // set by an untracked initial annotation. Surface that as a synthetic
      // "pre-history" row so the trail doesn't appear to start mid-stream.
      const oldest = entries.length > 0 ? entries[entries.length - 1] : null;
      if (
        oldest &&
        oldest.action !== 'create' &&
        oldest.previous_refactored_actual !== null &&
        oldest.previous_refactored_actual !== undefined
      ) {
        entries.push({
          id: `synthetic-${oldest.id}`,
          synthetic: true,
          created_at: null,
          action: 'create',
          previous_refactored_actual: null,
          refactored_actual: oldest.previous_refactored_actual,
          username: null,
          user_id: null,
        });
      }
      setHistoryEntries(entries);
    } catch (e) {
      setHistoryError(e?.response?.data?.message || e.message || 'Failed to load history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setHistoryLot(null);
    setHistoryEntries([]);
    setHistoryError('');
    setHistoryLoading(false);
  };

  const handleSelectImage = (url, images, lotNumber) => {
    setSelectedImage(url);
    setSelectedImages(images);
    setSelectedLotNum(lotNumber);
  };

  return (
    <div className="w-full max-w-none p-3 sm:p-6 bg-surface rounded-lg shadow-md">
      <DatasetSelector
        datasetDropdownRef={datasetDropdownRef}
        datasetBadgeText={datasetBadgeText}
        datasets={datasets}
        filteredDatasets={filteredDatasets}
        selectedDataset={selectedDataset}
        datasetSearch={datasetSearch}
        setDatasetSearch={setDatasetSearch}
        setSelectedDataset={setSelectedDataset}
        isDatasetDropdownOpen={isDatasetDropdownOpen}
        setIsDatasetDropdownOpen={setIsDatasetDropdownOpen}
        isLoadingDatasets={isLoadingDatasets}
        isRefreshingDatasets={isRefreshingDatasets}
        datasetLoadError={datasetLoadError}
        onRefreshDatasets={refreshDatasets}
        onSelectDataset={selectDataset}
        lotsCount={lots.length}
      />

      {datasetLoadError && (
        <div className="mb-3 rounded border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
          {datasetLoadError}
        </div>
      )}
      {error && (
        <div className="mb-3 rounded border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <AnnotationLotsTable
        loading={loading}
        lots={lots}
        drafts={drafts}
        saving={saving}
        selectedDataset={selectedDataset}
        currentUser={currentUser}
        onDraftChange={onDraftChange}
        onSaveLot={saveLot}
        onOpenHistory={openHistory}
        onSelectImage={handleSelectImage}
      />
      <AnnotationHistoryModal
        historyLot={historyLot}
        historyEntries={historyEntries}
        historyLoading={historyLoading}
        historyError={historyError}
        onClose={closeHistory}
      />
      <ImageModal
        isOpen={!!selectedImage}
        onClose={() => {
          setSelectedImage(null);
          setSelectedImages([]);
          setSelectedLotNum(null);
        }}
        imageUrl={selectedImage}
        imageUrls={selectedImages}
        lotNum={selectedLotNum}
      />
    </div>
  );
};

export default AnnotationPage;
